import type { VersusFighter, VersusUndercardMatch } from './versusBoardUtils'
import { mainEventPercents } from './versusBoardUtils'

type Props = {
  open: boolean
  matches: VersusUndercardMatch[]
  onClose: () => void
  title?: string
  matchLabelPrefix?: string
}

function StakeBar({ leftPct, rightPct }: { leftPct: number; rightPct: number }) {
  return (
    <div className="pred-undercard-modal-item__bar">
      <div
        className="pred-undercard-modal-item__bar-fill pred-undercard-modal-item__bar-fill--left"
        style={{ width: `${leftPct}%` }}
      />
      <div
        className="pred-undercard-modal-item__bar-fill pred-undercard-modal-item__bar-fill--right"
        style={{ width: `${rightPct}%` }}
      />
      <div className="pred-undercard-modal-item__bar-labels">
        <span>{leftPct.toFixed(1)}%</span>
        <span>{rightPct.toFixed(1)}%</span>
      </div>
    </div>
  )
}

function FighterSide({
  fighter,
  side,
}: {
  fighter: VersusFighter
  side: 'left' | 'right'
}) {
  const sideClass =
    side === 'left'
      ? 'pred-undercard-modal-item__side pred-undercard-modal-item__side--left'
      : 'pred-undercard-modal-item__side pred-undercard-modal-item__side--right'

  return (
    <div className={sideClass}>
      <div
        className={`pred-undercard-modal-item__portrait pred-undercard-modal-item__portrait--${side}`}
        style={{ '--fighter-color': fighter.barColor } as React.CSSProperties}
      >
        {fighter.imageUrl ? (
          <img src={fighter.imageUrl} alt={fighter.label} className="pred-undercard-modal-item__portrait-img" />
        ) : (
          <div className="pred-undercard-modal-item__portrait-fallback">
            {fighter.label.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>
      <div
        className="pred-undercard-modal-item__nameplate"
        style={{ backgroundColor: fighter.barColor }}
      >
        <div className="pred-undercard-modal-item__name">{fighter.label}</div>
        {fighter.subtitle ? <div className="pred-undercard-modal-item__sub">{fighter.subtitle}</div> : null}
      </div>
      <div className="pred-undercard-modal-item__stats">
        <div className="pred-undercard-modal-item__stat">
          <span className="pred-undercard-modal-item__stat-label">สัดส่วนแทง</span>
          <span className="pred-undercard-modal-item__stat-value">{fighter.stakePercent.toFixed(1)}%</span>
        </div>
        <div className="pred-undercard-modal-item__stat">
          <span className="pred-undercard-modal-item__stat-label">ยอดแทงรวม</span>
          <span className="pred-undercard-modal-item__stat-value">฿{fighter.totalStake.toLocaleString('th-TH')}</span>
        </div>
        {fighter.totalBets > 0 ? (
          <div className="pred-undercard-modal-item__stat">
            <span className="pred-undercard-modal-item__stat-label">จำนวนโพย</span>
            <span className="pred-undercard-modal-item__stat-value">{fighter.totalBets.toLocaleString('th-TH')}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default function UndercardDetailModal({
  open,
  matches,
  onClose,
  title = 'Undercard · คู่แข่งอื่น',
  matchLabelPrefix = 'คู่ที่',
}: Props) {
  if (!open) return null

  return (
    <div
      className="pred-modal-backdrop"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        className="pred-modal pred-modal--undercard"
        role="dialog"
        aria-modal="true"
        aria-labelledby="undercard-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pred-modal__header">
          <h3 id="undercard-modal-title" className="pred-modal__title">{title}</h3>
          <button type="button" className="pred-modal__close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </div>
        <div className="pred-modal__body">
          {matches.length === 0 ? (
            <p className="pred-empty mb-0">ไม่มีคู่รอง</p>
          ) : (
            matches.map((match, index) => {
              const { left: leftPct, right: rightPct } = mainEventPercents({ left: match.left, right: match.right })
              return (
                <div key={match.id} className="pred-undercard-modal-item">
                  <div className="pred-undercard-modal-item__head">
                    <span className="pred-badge">
                      {matchLabelPrefix === 'คู่ที่' ? `${matchLabelPrefix} ${index + 1}` : matchLabelPrefix}
                    </span>
                    {match.weightLabel ? (
                      <span className="pred-undercard-modal-item__weight">{match.weightLabel}</span>
                    ) : null}
                  </div>
                  <div className="pred-undercard-modal-item__showcase">
                    <FighterSide fighter={match.left} side="left" />
                    <div className="pred-undercard-modal-item__vs">
                      <div className="vs-badge vs-badge--mini" aria-hidden="true">VS</div>
                    </div>
                    <FighterSide fighter={match.right} side="right" />
                  </div>
                  <StakeBar leftPct={leftPct} rightPct={rightPct} />
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
